<template>
	<div class="chat-input-area">
		<div class="input-placeholder" v-show="isEmpty">{{ $t('chat.inputPlaceholder') }}</div>
		<div :class="['edit-container', isEmpty ? '' : 'not-empty']" contenteditable="true" @paste.prevent="onPaste"
			@keydown="onKeydown" @compositionstart="compositionFlag = true" @compositionend="onCompositionEnd"
			@input="onEditorInput" @mousedown="onMousedown" ref="content" @keyup="onKeyup" @click="onClickInput">
		</div>
		<chat-at-box @select="onAtSelect" :search-text="atSearchText" ref="atBox" :group="group"
			:members="groupMembers"></chat-at-box>
	</div>
</template>

<script>
import ChatAtBox from "./ChatAtBox";

export default {
	name: "ChatInput",
	components: { ChatAtBox },
	props: {
		group: {
			type: Object
		},
		groupMembers: {
			type: Array
		},
	},
	data() {
		return {
			draftMap: new Map(),
			imageList: [],
			fileList: [],
			currentId: 0,
			atSearchText: null,
			compositionFlag: false,
			atIng: false,
			isEmpty: true,
			blurRange: null
		}
	},
	methods: {
		// 将粘贴文本中的表情编码 #xxx; 转为表情图片插入
		insertPastedTextWithEmoji(range, txt) {
			const emojiPattern = /\#[\u4E00-\u9FA5]{1,3}\;/g;
			const parts = [];
			let lastIndex = 0;
			let match;
			while ((match = emojiPattern.exec(txt)) !== null) {
				if (match.index > lastIndex) {
					parts.push({ type: 'text', content: txt.substring(lastIndex, match.index) });
				}
				const code = match[0];
				const word = code.replace(/#|;/gi, '');
				if (this.$emo.emoTextList.indexOf(word) !== -1) {
					parts.push({ type: 'emoji', code });
				} else {
					parts.push({ type: 'text', content: code });
				}
				lastIndex = match.index + code.length;
			}
			if (lastIndex < txt.length) {
				parts.push({ type: 'text', content: txt.substring(lastIndex) });
			}
			if (parts.length === 0) {
				parts.push({ type: 'text', content: txt });
			}
			for (let i = 0; i < parts.length; i++) {
				const part = parts[i];
				let node;
				if (part.type === 'text') {
					node = document.createTextNode(part.content);
				} else {
					node = document.createElement('img');
					node.className = 'emoji-normal no-text';
					node.dataset.emojiCode = part.code;
					node.src = this.$emo.textToUrl(part.code);
				}
				range.insertNode(node);
				if (i < parts.length - 1) {
					range.setStartAfter(node);
					range.setEndAfter(node);
				}
			}
			range.collapse(false);
		},
		onPaste(e) {
			this.isEmpty = false;
			let txt = e.clipboardData.getData('Text')
			let range = window.getSelection().getRangeAt(0)
			if (range.startContainer !== range.endContainer || range.startOffset !== range.endOffset) {
				range.deleteContents();
			}
			// 粘贴图片和文件时，这里没有数据
			if (txt && typeof (txt) == 'string') {
				this.insertPastedTextWithEmoji(range, txt);
				return;
			}
			let items = (e.clipboardData || window.clipboardData).items
			if (items.length) {
				for (let i = 0; i < items.length; i++) {
					if (items[i].type.indexOf('image') !== -1) {
						let file = items[i].getAsFile();
						let imagePush = {
							fileId: this.generateId(),
							file: file,
							url: URL.createObjectURL(file)
						};
						this.imageList[imagePush.fileId] = (imagePush);
						let line = this.getInsertLine();
						let imageElement = document.createElement('img');
						imageElement.className = 'chat-image no-text';
						imageElement.src = imagePush.url;
						imageElement.dataset.imgId = imagePush.fileId;
						line.appendChild(imageElement);
						let after = document.createTextNode('\u00A0');
						line.appendChild(after);
						this.selectElement(after, 1);
					} else {
						let asFile = items[i].getAsFile();
						if (!asFile) {
							continue;
						}
						let filePush = { fileId: this.generateId(), file: asFile };
						this.fileList[filePush.fileId] = (filePush)
						let line = this.getInsertLine();
						let fileElement = this.createFile(filePush);
						line.appendChild(fileElement);
						let after = document.createTextNode('\u00A0');
						line.appendChild(after);
						this.selectElement(after, 1);
					}
				}
			}
			range.collapse();
		},
		pasteScreenShot(buffer) {
			let id = this.generateId();
			let file = new File([buffer], 'screenShot-' + id + '.png', { type: "image/png" }); //type为图片的格式
			let imagePush = {
				fileId: id,
				file: file,
				url: URL.createObjectURL(file)
			};
			this.imageList[imagePush.fileId] = (imagePush);
			let line = this.getInsertLine();
			let imageElement = document.createElement('img');
			imageElement.className = 'chat-image no-text';
			imageElement.src = imagePush.url;
			imageElement.dataset.imgId = imagePush.fileId;
			line.appendChild(imageElement);
			let after = document.createTextNode('\u00A0');
			line.appendChild(after);
			this.selectElement(after, 1);
		},
		selectElement(element, endOffset) {
			let selection = window.getSelection();
			// 插入元素可能不是立即执行的，vue可能会在插入元素后再更新dom
			this.$nextTick(() => {
				let t1 = document.createRange();
				t1.setStart(element, 0);
				t1.setEnd(element, endOffset || 0);
				if (element.firstChild) {
					t1.selectNodeContents(element.firstChild);
				}
				t1.collapse();
				selection.removeAllRanges();
				selection.addRange(t1);
				// 需要时自动聚焦
				if (element.focus) {
					element.focus();
				}
			})
		},
		onCompositionEnd(e) {
			this.compositionFlag = false;
			this.onEditorInput(e);
		},
		onKeydown(e) {
			if (e.keyCode === 13) {
				e.preventDefault();
				e.stopPropagation();
				if (this.atIng) {
					this.$refs.atBox.select();
					return;
				}
				if (e.ctrlKey) {
					let selection = window.getSelection();
					let currentRange = selection.getRangeAt(0);
					let currentContainer = currentRange.endContainer;
					// 如果光标在零宽度空格节点中，先移动到父节点，避免影响 newLine() 的识别
					if (currentContainer.nodeType === 3 && currentContainer.textContent === '\u200B') {
						let parent = currentContainer.parentNode;
						let offset = Array.from(parent.childNodes).indexOf(currentContainer);
						currentRange.setStart(parent, offset);
						currentRange.setEnd(parent, offset);
						currentRange.collapse(true);
						selection.removeAllRanges();
						selection.addRange(currentRange);
					}
					let line = this.newLine();
					// 使用零宽度空格节点（\u200B）用于光标定位，不可见，不影响对齐
					let zeroWidthSpace = document.createTextNode('\u200B');
					line.appendChild(zeroWidthSpace);
					// 同步设置光标位置，确保连续换行时能正确定位
					let range = document.createRange();
					range.setStart(zeroWidthSpace, 0);
					range.setEnd(zeroWidthSpace, 0);
					range.collapse(true);
					selection.removeAllRanges();
					selection.addRange(range);
					// 同步更新 blurRange，确保下次操作使用正确位置
					this.blurRange = range.cloneRange();
					this.isEmpty = false;
				} else {
					// 中文输入标记
					if (this.compositionFlag) {
						return;
					}
					this.submit();
				}
				return;
			}
			// 删除键
			if (e.keyCode === 8) {
				// 等待dom更新
				setTimeout(() => {
					let content = this.$refs.content;
					// 用 textContent 检测是否为空，去除零宽空格、不间断空格和空白字符
					let textContent = content.textContent.replace(/[\u200B\u00A0\s]/g, '');
					// 检测是否还有内容元素（图片、文件、@用户，表情不算）
					let hasMedia = content.querySelector('img[data-img-id], .chat-file-container, .chat-at-user');
					// 空dom时，需要刷新dom
					if (textContent === '' && !hasMedia) {
						// 拼接随机长度的空格，以刷新dom
						this.empty();
						this.isEmpty = true;
						this.selectElement(this.$refs.content);
					} else {
						this.isEmpty = false;
					}
				})
			}
			// at框打开时，上下键移动特殊处理
			if (this.atIng) {
				if (e.keyCode === 38) {
					e.preventDefault();
					e.stopPropagation();
					this.$refs.atBox.moveUp();
				}
				if (e.keyCode === 40) {
					e.preventDefault();
					e.stopPropagation();
					this.$refs.atBox.moveDown();
				}
			}
		},
		insertAtMember(member) {
			this.focus();
			this.$nextTick(() => {
				this.updateRange();
				let blurRange = this.blurRange;
				if (!blurRange) {
					return;
				}
				if (blurRange.startContainer !== blurRange.endContainer || blurRange.startOffset !== blurRange.endOffset) {
					blurRange.deleteContents();
				}
				let element = document.createElement('SPAN');
				element.className = 'chat-at-user';
				element.dataset.id = member.userId;
				element.contentEditable = 'false';
				element.innerText = `@${member.showNickName}`;
				blurRange.insertNode(element);
				blurRange.collapse(false);
				let textNode = document.createTextNode('\u00A0');
				blurRange.insertNode(textNode);
				blurRange.collapse();
				this.selectElement(textNode, 1);
				this.isEmpty = false;
			});
		},
		onAtSelect(member) {
			this.atIng = false;
			// 选中输入的 @xx 符
			let blurRange = this.blurRange;
			let endContainer = blurRange.endContainer
			let startOffset = endContainer.data.indexOf("@" + this.atSearchText);
			let endOffset = startOffset + this.atSearchText.length + 1;
			blurRange.setStart(blurRange.endContainer, startOffset);
			blurRange.setEnd(blurRange.endContainer, endOffset);
			blurRange.deleteContents()
			blurRange.collapse();
			this.focus();
			// 创建元素节点
			let element = document.createElement('SPAN')
			element.className = "chat-at-user";
			element.dataset.id = member.userId;
			element.contentEditable = 'false'
			element.innerText = `@${member.showNickName}`
			blurRange.insertNode(element)
			// 光标移动到末尾
			blurRange.collapse()

			// 插入空格
			let textNode = document.createTextNode('\u00A0');
			blurRange.insertNode(textNode);

			blurRange.collapse()
			this.atSearchText = "";
			this.selectElement(textNode, 1);
		},
		onEditorInput(e) {
			this.isEmpty = false;
			if (this.$props.groupMembers && !this.compositionFlag) {
				let selection = window.getSelection()
				let range = selection.getRangeAt(0);
				// 截取@后面的名称作为过滤条件，并以空格结束
				let endContainer = range.endContainer;
				let endOffset = range.endOffset;
				let textContent = endContainer.textContent;
				let startIndex = -1;
				for (let i = endOffset; i >= 0; i--) {
					if (textContent[i] === '@') {
						startIndex = i;
						break;
					}
				}
				// 没有at符号，则关闭弹窗
				if (startIndex === -1) {
					this.$refs.atBox.close();
					return;
				}

				let endIndex = endOffset;
				for (let i = endOffset; i < textContent.length; i++) {
					if (textContent[i] === ' ') {
						endIndex = i;
						break;
					}
				}
				this.atSearchText = textContent.substring(startIndex + 1, endIndex).trim();
				// 打开选择弹窗
				if (this.atSearchText == '') {
					this.showAtBox(e)
				}
			}
		},
		onClickInput() {
			this.updateRange();
		},
		onKeyup() {
			this.updateRange();
		},
		onMousedown() {
			if (this.atIng) {
				this.$refs.atBox.close();
				this.atIng = false;
			}
		},
		insertEmoji(emojiText) {
			let emojiElement = document.createElement('img');
			emojiElement.className = 'emoji-normal no-text';
			emojiElement.dataset.emojiCode = emojiText;
			emojiElement.src = this.$emo.textToUrl(emojiText);

			let blurRange = this.blurRange;
			if (!blurRange) {
				this.focus();
				this.updateRange();
				blurRange = this.blurRange;
			}
			if (blurRange.startContainer !== blurRange.endContainer || blurRange.startOffset !== blurRange.endOffset) {
				blurRange.deleteContents();
			}
			blurRange.insertNode(emojiElement);
			blurRange.collapse()

			let textNode = document.createTextNode('\u00A0');
			blurRange.insertNode(textNode)
			blurRange.collapse()

			this.selectElement(textNode);
			this.isEmpty = false;
		},
		generateId() {
			return this.currentId++;
		},
		createFile(filePush) {
			let file = filePush.file;
			let fileId = filePush.fileId;
			let container = document.createElement('div');
			container.className = 'chat-file-container no-text';
			container.contentEditable = 'false';
			container.dataset.fileId = fileId;

			let left = document.createElement('div');
			left.className = 'file-position-left';
			container.appendChild(left);

			let icon = document.createElement('div');
			icon.className = 'el-icon-document';
			left.appendChild(icon);

			let right = document.createElement('div');
			right.className = 'file-position-right';
			container.appendChild(right);

			let fileName = document.createElement('div');
			fileName.className = 'file-name';
			fileName.innerText = file.name;

			let fileSize = document.createElement('div');
			fileSize.className = 'file-size';
			fileSize.innerText = this.sizeConvert(file.size);

			right.appendChild(fileName);
			right.appendChild(fileSize);

			return container;
		},
		sizeConvert(len) {
			if (len < 1024) {
				return len + 'B';
			} else if (len < 1024 * 1024) {
				return (len / 1024).toFixed(2) + 'KB';
			} else if (len < 1024 * 1024 * 1024) {
				return (len / 1024 / 1024).toFixed(2) + 'MB';
			} else {
				return (len / 1024 / 1024 / 1024).toFixed(2) + 'GB';
			}
		},
		updateRange() {
			let selection = window.getSelection();
			this.blurRange = selection.getRangeAt(0);
		},
		newLine() {
			let selection = window.getSelection();
			let range = selection.getRangeAt(0);
			let divElement = document.createElement('div');
			let endContainer = range.endContainer;
			let parentElement = endContainer.parentElement;
			if (parentElement.parentElement === this.$refs.content) {
				divElement.innerHTML = endContainer.textContent.substring(range.endOffset).trim();
				endContainer.textContent = endContainer.textContent.substring(0, range.endOffset);
				// 插入到当前div（当前行）后面
				parentElement.insertAdjacentElement('afterend', divElement);
			} else {
				divElement.innerHTML = '';
				this.$refs.content.append(divElement);
			}
			return divElement;
		},
		// 获取插入行：如果当前行为空（无文字、无图片、无文件、无@用户），复用该行，避免多出空行
		getInsertLine() {
			let selection = window.getSelection();
			let range = selection.getRangeAt(0);
			let endContainer = range.endContainer;
			let lineDiv = null;
			if (endContainer.nodeType === 3) {
				// 光标在文本节点中，父元素即为行div
				if (endContainer.parentElement && endContainer.parentElement.parentElement === this.$refs.content) {
					lineDiv = endContainer.parentElement;
				}
			} else if (endContainer.nodeType === 1 && endContainer.parentElement === this.$refs.content) {
				// 光标在行div上
				lineDiv = endContainer;
			}
			if (lineDiv) {
				let lineText = lineDiv.textContent.replace(/[\u200B\u00A0\s]/g, '');
				let hasMedia = lineDiv.querySelector('img[data-img-id], .chat-file-container, .chat-at-user');
				if (lineText === '' && !hasMedia) {
					lineDiv.innerHTML = '';
					return lineDiv;
				}
			}
			return this.newLine();
		},
		clear() {
			this.empty();
			this.imageList = [];
			this.fileList = [];
			this.$refs.atBox.close();
		},
		empty() {
			this.$refs.content.innerHTML = "";
			let line = this.newLine();
			// 使用零宽度空格（\u200B）用于光标定位，视觉上不可见，避免显示多余的空格
			let after = document.createTextNode('\u200B');
			line.appendChild(after);
			this.isEmpty = true;
			this.$nextTick(() => this.selectElement(after));
		},
		showAtBox() {
			this.atIng = true;
			// show之后会自动更新当前搜索的text
			// this.atSearchText = "";
			let selection = window.getSelection()
			let range = selection.getRangeAt(0)
			// 光标所在坐标
			let pos = range.getBoundingClientRect();
			this.$refs.atBox.open({
				x: pos.x,
				y: pos.y
			})
			// 记录光标所在位置
			this.updateRange();
		},
		submit() {
			let nodes = this.$refs.content.childNodes;
			let fullList = [];
			let tempText = '';
			let atUserIds = [];
			let each = (nodes) => {
				for (let i = 0; i < nodes.length; i++) {
					let node = nodes[i];
					if (!node) {
						continue;
					}
					if (node.nodeType === 3) {
						tempText += node.textContent;
						continue;
					}
					let nodeName = node.nodeName.toLowerCase();
					if (nodeName === 'script') {
						continue;
					}
					let text = tempText.replace(/\u200B/g, '').trim();
					if (nodeName === 'img') {
						let imgId = node.dataset.imgId;
						if (imgId) {
							if (text) {
								fullList.push({
									type: 'text',
									content: text,
									atUserIds: atUserIds
								})
								tempText = '';
								atUserIds = []
							}
							fullList.push({
								type: 'image',
								content: this.imageList[imgId]
							})
						} else {
							let emojiCode = node.dataset.emojiCode;
							tempText += emojiCode;
						}
					} else if (nodeName === 'div') {
						let fileId = node.dataset.fileId
						// 文件
						if (fileId) {
							if (text) {
								fullList.push({
									type: 'text',
									content: text,
									atUserIds: atUserIds
								})
								tempText = '';
								atUserIds = []
							}
							fullList.push({
								type: 'file',
								content: this.fileList[fileId]
							})
						} else {
							tempText += '\n';
							each(node.childNodes);
						}
					} else if (nodeName === 'span') {
						if (node.dataset.id) {
							tempText += node.innerHTML;
							atUserIds.push(node.dataset.id)
						} else if (node.outerHtml) {
							tempText += node.outerHtml;
						}
					}
				}
			}
			each(nodes)
			let text = tempText.replace(/\u200B/g, '').trim();
			if (text !== '') {
				fullList.push({
					type: 'text',
					content: text,
					atUserIds: atUserIds
				})
			}
			this.$emit('submit', fullList);
		},
		focus() {
			this.$refs.content.focus();
		},
		setText(content) {
			if (this.isEmpty) {
				this.$refs.content.innerHTML = "";
			}
			this.$refs.content.appendChild(document.createTextNode(content));
			this.isEmpty = !this.$refs.content.innerText;
			this.moveCursorToEnd();
		},
		saveDraft(conv) {
			if (!conv) return;
			const draft = {
				innerHTML: this.$refs.content.innerHTML,
				imageList: this.imageList,
				fileList: this.fileList,
				isEmpty: this.isEmpty
			}
			this.draftMap.set(conv.key, draft);
			this.chatStore.setDraftText(conv.key, this.draftText())
		},
		restoreDraft(conv) {
			if (!conv) return;
			const draft = this.draftMap.get(conv.key);
			if (draft && !draft.isEmpty) {
				this.$refs.content.innerHTML = draft.innerHTML;
				this.imageList = draft.imageList;
				this.fileList = draft.fileList;
				this.isEmpty = draft.isEmpty;
				this.chatStore.setDraftText(conv.key, "");
				// 将光标移动到末尾
				this.moveCursorToEnd();
			} else {
				this.clear();
			}
		},
		moveCursorToEnd() {
			const content = this.$refs.content;
			const range = document.createRange();
			const selection = window.getSelection();
			// 将光标设置到 content 元素的末尾
			range.selectNodeContents(content);
			// false表示折叠到范围的末尾
			range.collapse(false);
			selection.removeAllRanges();
			selection.addRange(range);
			this.focus();
			this.updateRange();
		},
		draftText() {
			let nodes = this.$refs.content.childNodes;
			let tempText = '';
			let each = (nodes) => {
				for (let i = 0; i < nodes.length; i++) {
					let node = nodes[i];
					if (!node) {
						continue;
					}
					if (node.nodeType === 3) {
						tempText += node.textContent;
						continue;
					}
					let nodeName = node.nodeName.toLowerCase();
					if (nodeName === 'script') {
						continue;
					}
					if (nodeName === 'img') {
						let imgId = node.dataset.imgId;
						if (imgId) {
							tempText += this.$t('chat.previewImage');
						} else {
							let emojiCode = node.dataset.emojiCode;
							tempText += emojiCode;
						}
					} else if (nodeName === 'div') {
						let fileId = node.dataset.fileId
						// 文件
						if (fileId) {
							tempText += this.$t('chat.previewFile');
						} else {
							tempText += '\n';
							each(node.childNodes);
						}
					} else if (nodeName === 'span') {
						if (node.dataset.id) {
							tempText += node.innerHTML;
						} else if (node.outerHtml) {
							tempText += node.outerHtml;
						}
					}
				}
			}
			each(nodes)
			return tempText.replace(/\u200B/g, '').trim();
		}
	}
}
</script>

<style lang="scss">
.chat-input-area {
	width: 100%;
	height: 100%;
	position: relative;

	.input-placeholder {
		position: absolute;
		top: 8px;
		left: 20px;
		color: var(--im-text-color-lighter);
		pointer-events: none;
		font-size: var(--im-font-size);
		line-height: 26px;
	}

	.edit-container {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		outline: none;
		padding: 8px 12px 0 20px;
		line-height: 26px;
		font-size: var(--im-font-size);
		text-align: left;
		overflow-y: auto;
		white-space: pre-wrap;

		// 单独一行时，无法在前面输入的bug
		>div:before {
			content: "\00a0";
			font-size: 14px;
			position: absolute;
			top: 0;
			left: 0;
		}

		.chat-image {
			display: block;
			max-width: 200px;
			max-height: 100px;
			border: 1px solid #e6e6e6;
			cursor: pointer;
		}

		.chat-file-container {
			max-width: 65%;
			padding: 10px;
			border: 2px solid #587ff0;
			display: flex;
			background: #eeeC;
			border-radius: 10px;

			.file-position-left {
				display: flex;
				width: 80px;
				justify-content: center;
				align-items: center;

				.el-icon-document {
					font-size: 40px;
					text-align: center;
					color: #d42e07;
				}
			}

			.file-position-right {
				flex: 1;

				.file-name {
					font-size: 16px;
					font-weight: 600;
					color: #66b1ff;
				}

				.file-size {
					font-size: 14px;
					font-weight: 600;
				}
			}
		}

		.chat-at-user {
			color: #00f;
			border-radius: 3px;
		}
	}
}
</style>
